import {
  RELAY_CLIENT_FRAME_LIMIT_BYTES,
  RELAY_MAILBOX_PATH,
  RELAY_MAILBOX_WRITE_HEADER,
  resolveSameOriginRelayEndpoint,
} from "./relay-contract";

export type RoomInvite = {
  version: 1;
  roomId: string;
  ticket: string;
  creatorClientId: string;
  creatorEphemeralPublicKey: JsonWebKey;
  creatorIdentityPublicKey: JsonWebKey;
  expiresAt: number;
  signature: string;
};

export type RelayFrame = {
  id: string;
  kind: "hello" | "ready" | "message";
  senderId: string;
  sequence: number;
  payload: string;
};

export type RoomRelayStatus = "idle" | "creating" | "joining" | "waiting-peer" | "connected" | "closed" | "error";

export type RoomRelayOptions = {
  endpoint?: string;
  onStatus?: (status: RoomRelayStatus, detail?: string) => void;
  onMessage?: (message: string, frame: RelayFrame) => void;
};

const INVITE_PREFIX = "browser-local-room-v1.";
const JOIN_LANE = "join";
const PROTOCOL_LABEL = "browser-local-same-origin-cache-mailbox-v1";
const POLL_DELAY_MS = 450;
const ROOM_TTL_MS = 110 * 1000;
const MAX_WRITE_ATTEMPTS = 3;
const MAX_RETRY_DELAY_MS = 3_000;
const encoder = new TextEncoder();
const decoder = new TextDecoder();

type RoomClientKeys = {
  ecdh: CryptoKeyPair;
  identity: CryptoKeyPair;
  publicKey: JsonWebKey;
  identityPublicKey: JsonWebKey;
};

type HelloPayload = {
  type: "hello";
  roomId: string;
  senderId: string;
  ephemeralPublicKey: JsonWebKey;
  identityPublicKey: JsonWebKey;
  signature: string;
};

type ReadyPayload = {
  type: "ready";
  fingerprint: string;
};

function bytesToBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.slice().buffer as ArrayBuffer;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

function base64UrlToBytes(value: string): Uint8Array {
  const normalized = value.replaceAll("-", "+").replaceAll("_", "/") + "===".slice((value.length + 3) % 4);
  const binary = atob(normalized);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

function encodeText(value: string): string {
  return bytesToBase64Url(encoder.encode(value));
}

function decodeText(value: string): string {
  return decoder.decode(base64UrlToBytes(value));
}

function stableJson(value: unknown): string {
  return JSON.stringify(value);
}

async function sha256(value: string): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest("SHA-256", bytesToBuffer(encoder.encode(value))));
}

async function importEcdhPublicKey(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey("jwk", jwk, { name: "ECDH", namedCurve: "P-256" }, true, []);
}

async function importIdentityPublicKey(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey("jwk", jwk, { name: "ECDSA", namedCurve: "P-256" }, true, ["verify"]);
}

async function sign(identity: CryptoKey, value: string): Promise<string> {
  const signature = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, identity, bytesToBuffer(encoder.encode(value)));
  return bytesToBase64Url(new Uint8Array(signature));
}

async function verify(identity: CryptoKey, value: string, signature: string): Promise<boolean> {
  return crypto.subtle.verify(
    { name: "ECDSA", hash: "SHA-256" },
    identity,
    bytesToBuffer(base64UrlToBytes(signature)),
    bytesToBuffer(encoder.encode(value)),
  );
}

async function generateKeys(): Promise<RoomClientKeys> {
  const [ecdh, identity] = await Promise.all([
    crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, true, ["deriveBits"]),
    crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, true, ["sign", "verify"]),
  ]);
  return {
    ecdh,
    identity,
    publicKey: await crypto.subtle.exportKey("jwk", ecdh.publicKey),
    identityPublicKey: await crypto.subtle.exportKey("jwk", identity.publicKey),
  };
}

function randomToken(bytes = 24): string {
  const value = new Uint8Array(bytes);
  crypto.getRandomValues(value);
  return bytesToBase64Url(value);
}

function inviteUnsignedBody(invite: Omit<RoomInvite, "signature">): string {
  return stableJson({
    version: invite.version,
    roomId: invite.roomId,
    ticket: invite.ticket,
    creatorClientId: invite.creatorClientId,
    creatorEphemeralPublicKey: invite.creatorEphemeralPublicKey,
    creatorIdentityPublicKey: invite.creatorIdentityPublicKey,
    expiresAt: invite.expiresAt,
  });
}

function helloUnsignedBody(hello: Omit<HelloPayload, "signature">): string {
  return stableJson({
    type: hello.type,
    roomId: hello.roomId,
    senderId: hello.senderId,
    ephemeralPublicKey: hello.ephemeralPublicKey,
    identityPublicKey: hello.identityPublicKey,
  });
}

async function deriveRoomKey(
  ownPrivateKey: CryptoKey,
  peerPublicJwk: JsonWebKey,
  roomId: string,
  creatorPublicKey: JsonWebKey,
  joinerPublicKey: JsonWebKey,
): Promise<CryptoKey> {
  const peerPublic = await importEcdhPublicKey(peerPublicJwk);
  const sharedBits = await crypto.subtle.deriveBits({ name: "ECDH", public: peerPublic }, ownPrivateKey, 256);
  const hkdfKey = await crypto.subtle.importKey("raw", bytesToBuffer(new Uint8Array(sharedBits)), "HKDF", false, ["deriveKey"]);
  const transcript = stableJson({ roomId, creatorPublicKey, joinerPublicKey });
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: bytesToBuffer(encoder.encode(PROTOCOL_LABEL)),
      info: bytesToBuffer(encoder.encode(transcript)),
    },
    hkdfKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

async function encrypt(key: CryptoKey, value: unknown, aad: string): Promise<string> {
  const nonce = new Uint8Array(12);
  crypto.getRandomValues(nonce);
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: bytesToBuffer(nonce), additionalData: bytesToBuffer(encoder.encode(aad)) },
    key,
    bytesToBuffer(encoder.encode(JSON.stringify(value))),
  );
  return `${bytesToBase64Url(nonce)}.${bytesToBase64Url(new Uint8Array(ciphertext))}`;
}

async function decrypt<T>(key: CryptoKey, value: string, aad: string): Promise<T> {
  const [nonceValue, ciphertextValue] = value.split(".");
  if (!nonceValue || !ciphertextValue) throw new Error("encrypted_frame_invalid");
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: bytesToBuffer(base64UrlToBytes(nonceValue)), additionalData: bytesToBuffer(encoder.encode(aad)) },
    key,
    bytesToBuffer(base64UrlToBytes(ciphertextValue)),
  );
  return JSON.parse(decoder.decode(plaintext)) as T;
}

export class SameOriginRoomClient {
  readonly clientId = randomToken(18);
  private readonly endpoint: string;
  private readonly onStatusChange?: RoomRelayOptions["onStatus"];
  private readonly onMessageCallback?: RoomRelayOptions["onMessage"];
  private status: RoomRelayStatus = "idle";
  private roomId?: string;
  private ticket?: string;
  private expiresAt?: number;
  private keys?: RoomClientKeys;
  private roomKey?: CryptoKey;
  private invite?: RoomInvite;
  private peerClientId?: string;
  private peerPublicKey?: JsonWebKey;
  private sendSequence = 1;
  private abortController?: AbortController;
  private polling = new Map<string, Promise<void>>();

  constructor(options: RoomRelayOptions = {}) {
    this.endpoint = resolveSameOriginRelayEndpoint(options.endpoint ?? RELAY_MAILBOX_PATH, window.location.href);
    this.onStatusChange = options.onStatus;
    this.onMessageCallback = options.onMessage;
  }

  get currentStatus(): RoomRelayStatus { return this.status; }
  get currentRoomId(): string | undefined { return this.roomId; }

  async createRoom(): Promise<string> {
    await this.close();
    this.setStatus("creating");
    this.keys = await generateKeys();
    this.roomId = randomToken(16);
    this.ticket = randomToken(24);
    this.expiresAt = Date.now() + ROOM_TTL_MS;
    const unsignedInvite: Omit<RoomInvite, "signature"> = {
      version: 1,
      roomId: this.roomId,
      ticket: this.ticket,
      creatorClientId: this.clientId,
      creatorEphemeralPublicKey: this.keys.publicKey,
      creatorIdentityPublicKey: this.keys.identityPublicKey,
      expiresAt: this.expiresAt,
    };
    const invite: RoomInvite = {
      ...unsignedInvite,
      signature: await sign(this.keys.identity.privateKey, inviteUnsignedBody(unsignedInvite)),
    };
    this.invite = invite;
    this.startPolling(JOIN_LANE, 1);
    this.setStatus("waiting-peer");
    const inviteUrl = new URL(window.location.href);
    inviteUrl.search = "";
    inviteUrl.hash = `invite=${INVITE_PREFIX}${encodeText(JSON.stringify(invite))}`;
    return inviteUrl.toString();
  }

  async joinInvite(value: string): Promise<void> {
    await this.close();
    this.setStatus("joining");
    const invite = SameOriginRoomClient.decodeInvite(value);
    if (invite.expiresAt <= Date.now()) throw new Error("invite_expired");
    const creatorIdentity = await importIdentityPublicKey(invite.creatorIdentityPublicKey);
    if (!(await verify(creatorIdentity, inviteUnsignedBody(invite), invite.signature))) throw new Error("invite_signature_invalid");
    this.keys = await generateKeys();
    this.roomId = invite.roomId;
    this.ticket = invite.ticket;
    this.expiresAt = invite.expiresAt;
    this.invite = invite;
    this.peerClientId = invite.creatorClientId;
    this.roomKey = await deriveRoomKey(this.keys.ecdh.privateKey, invite.creatorEphemeralPublicKey, this.roomId, invite.creatorEphemeralPublicKey, this.keys.publicKey);
    this.startPolling(invite.creatorClientId, 1);
    const hello: HelloPayload = {
      type: "hello",
      roomId: this.roomId,
      senderId: this.clientId,
      ephemeralPublicKey: this.keys.publicKey,
      identityPublicKey: this.keys.identityPublicKey,
      signature: "",
    };
    hello.signature = await sign(this.keys.identity.privateKey, helloUnsignedBody(hello));
    await this.publishFrame({ id: randomToken(18), kind: "hello", senderId: this.clientId, sequence: 1, payload: encodeText(JSON.stringify(hello)) }, JOIN_LANE, 1);
    this.setStatus("waiting-peer");
  }

  async send(message: string): Promise<void> {
    if (!this.roomKey || !this.roomId || !this.peerClientId) throw new Error("room_not_ready");
    if (!message.trim()) throw new Error("message_empty");
    if (message.length > 2_000) throw new Error("message_too_large");
    const sequence = this.sendSequence;
    const payload = await encrypt(this.roomKey, { type: "message", message }, `${this.roomId}:${this.clientId}:${sequence}`);
    await this.publishFrame({ id: randomToken(18), kind: "message", senderId: this.clientId, sequence, payload }, this.clientId, sequence);
    this.sendSequence += 1;
  }

  async close(): Promise<void> {
    this.abortController?.abort();
    await Promise.all([...this.polling.values()].map((promise) => promise.catch(() => undefined)));
    this.abortController = undefined;
    this.polling.clear();
    this.roomId = undefined;
    this.ticket = undefined;
    this.expiresAt = undefined;
    this.keys = undefined;
    this.roomKey = undefined;
    this.invite = undefined;
    this.peerClientId = undefined;
    this.peerPublicKey = undefined;
    this.sendSequence = 1;
    this.setStatus("closed");
  }

  static decodeInvite(value: string): RoomInvite {
    const encoded = value.includes("#invite=") ? value.split("#invite=")[1] : value;
    const payload = encoded.startsWith(INVITE_PREFIX) ? encoded.slice(INVITE_PREFIX.length) : encoded;
    const invite = JSON.parse(decodeText(payload)) as RoomInvite;
    if (invite.version !== 1 || !invite.roomId || !invite.ticket || !invite.creatorClientId || !invite.signature) throw new Error("invite_invalid");
    return invite;
  }

  private setStatus(status: RoomRelayStatus, detail?: string): void {
    this.status = status;
    this.onStatusChange?.(status, detail);
  }

  private mailboxUrl(sender: string, sequence: number): string {
    if (!this.ticket) throw new Error("room_not_created");
    const params = new URLSearchParams({ room: this.ticket, sender, seq: String(sequence) });
    return `${this.endpoint}?${params.toString()}`;
  }

  private async publishFrame(frame: RelayFrame, sender: string, sequence: number): Promise<void> {
    const encoded = encodeText(JSON.stringify(frame));
    if (encoded.length > RELAY_CLIENT_FRAME_LIMIT_BYTES) throw new Error("encrypted_frame_too_large");
    let lastError: unknown;
    for (let attempt = 1; attempt <= MAX_WRITE_ATTEMPTS; attempt += 1) {
      if (this.abortController?.signal.aborted) throw new Error("room_closed");
      try {
        const response = await fetch(this.mailboxUrl(sender, sequence), {
          method: "GET",
          cache: "default",
          credentials: "same-origin",
          headers: { [RELAY_MAILBOX_WRITE_HEADER]: encoded },
        });
        if (response.ok) return;
        if (response.status < 500 && response.status !== 408 && response.status !== 429) {
          throw new Error(`mailbox_write_failed_${response.status}`);
        }
        lastError = new Error(`mailbox_write_failed_${response.status}`);
      } catch (error) {
        lastError = error;
      }
      if (this.abortController?.signal.aborted) throw new Error("room_closed");
      if (attempt < MAX_WRITE_ATTEMPTS) {
        await this.wait(Math.min(MAX_RETRY_DELAY_MS, POLL_DELAY_MS * 2 ** (attempt - 1)));
      }
    }
    throw lastError instanceof Error ? lastError : new Error("mailbox_write_failed");
  }

  private startPolling(sender: string, firstSequence: number): void {
    if (this.polling.has(sender)) return;
    const promise = this.pollLane(sender, firstSequence).finally(() => this.polling.delete(sender));
    this.polling.set(sender, promise);
  }

  private async pollLane(sender: string, firstSequence: number): Promise<void> {
    this.abortController ??= new AbortController();
    const signal = this.abortController.signal;
    let sequence = firstSequence;
    let transientFailures = 0;
    while (!signal.aborted && (!this.expiresAt || this.expiresAt > Date.now())) {
      let response: Response;
      try {
        response = await fetch(this.mailboxUrl(sender, sequence), {
          method: "GET",
          cache: "default",
          credentials: "same-origin",
          signal,
        });
        transientFailures = 0;
      } catch (error) {
        if (signal.aborted || (error instanceof DOMException && error.name === "AbortError")) return;
        transientFailures += 1;
        this.setStatus("error", "Mailbox unavailable; retrying automatically…");
        await this.wait(Math.min(MAX_RETRY_DELAY_MS, POLL_DELAY_MS * 2 ** Math.min(transientFailures - 1, 3)));
        if (!signal.aborted) this.restoreOperationalStatus("Mailbox retrying…");
        continue;
      }
      if (response.status === 200) {
        try {
          const encoded = await response.text();
          const frame = JSON.parse(decodeText(encoded)) as RelayFrame;
          sequence = Math.max(sequence + 1, frame.sequence + 1);
          await this.handleFrame(frame);
        } catch (error) {
          this.setStatus("error", `Invalid relay frame ignored: ${errorMessage(error)}`);
        }
      } else if (response.status === 404) {
        await this.wait(POLL_DELAY_MS);
      } else if (response.status >= 500 || response.status === 408 || response.status === 429) {
        transientFailures += 1;
        this.setStatus("error", `Mailbox returned HTTP ${response.status}; retrying…`);
        await this.wait(Math.min(MAX_RETRY_DELAY_MS, POLL_DELAY_MS * 2 ** Math.min(transientFailures - 1, 3)));
        if (!signal.aborted) this.restoreOperationalStatus("Mailbox retrying…");
      } else {
        throw new Error(`mailbox_read_failed_${response.status}`);
      }
    }
    if (!signal.aborted && this.expiresAt !== undefined && this.expiresAt <= Date.now()) {
      this.setStatus("closed", "Room expired; create a new room to continue.");
    }
  }

  private async wait(delayMs: number): Promise<void> {
    await new Promise<void>((resolve) => window.setTimeout(resolve, delayMs));
  }

  private restoreOperationalStatus(detail: string): void {
    if (this.roomKey && this.peerClientId) {
      this.setStatus("connected", detail);
    } else {
      this.setStatus("waiting-peer", detail);
    }
  }

  private async handleFrame(frame: RelayFrame): Promise<void> {
    if (!this.roomId || !this.keys || frame.senderId === this.clientId) return;
    if (frame.kind === "hello") {
      const hello = JSON.parse(decodeText(frame.payload)) as HelloPayload;
      if (hello.roomId !== this.roomId) throw new Error("peer_room_mismatch");
      const peerIdentity = await importIdentityPublicKey(hello.identityPublicKey);
      if (!(await verify(peerIdentity, helloUnsignedBody(hello), hello.signature))) throw new Error("peer_signature_invalid");
      this.peerClientId = hello.senderId;
      this.peerPublicKey = hello.ephemeralPublicKey;
      this.roomKey = await deriveRoomKey(this.keys.ecdh.privateKey, hello.ephemeralPublicKey, this.roomId, this.invite?.creatorEphemeralPublicKey ?? hello.ephemeralPublicKey, hello.ephemeralPublicKey);
      this.startPolling(hello.senderId, 1);
      const fingerprint = bytesToBase64Url(await sha256(stableJson({ roomId: this.roomId, creator: this.invite?.creatorEphemeralPublicKey, joiner: hello.ephemeralPublicKey }))).slice(0, 16);
      const readySequence = this.sendSequence;
      const ready = await encrypt(this.roomKey, { type: "ready", fingerprint } satisfies ReadyPayload, `${this.roomId}:${this.clientId}:${readySequence}`);
      await this.publishFrame({ id: randomToken(18), kind: "ready", senderId: this.clientId, sequence: readySequence, payload: ready }, this.clientId, readySequence);
      this.sendSequence += 1;
      this.setStatus("connected", `fingerprint:${fingerprint}`);
      return;
    }
    if (!this.roomKey) return;
    const aad = `${this.roomId}:${frame.senderId}:${frame.sequence}`;
    if (frame.kind === "ready") {
      const ready = await decrypt<ReadyPayload>(this.roomKey, frame.payload, aad);
      this.setStatus("connected", `fingerprint:${ready.fingerprint}`);
      return;
    }
    if (frame.kind === "message") {
      const data = await decrypt<{ type: "message"; message: string }>(this.roomKey, frame.payload, aad);
      if (data.type !== "message" || typeof data.message !== "string" || data.message.length > 2_000) {
        throw new Error("message_invalid");
      }
      this.onMessageCallback?.(data.message, frame);
    }
  }
}

export const ROOM_RELAY_ENDPOINT = RELAY_MAILBOX_PATH;

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
